import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import type { NodeKey } from 'lexical';
import { $getNodeByKey, $getSelection, $isNodeSelection, COMMAND_PRIORITY_HIGH, KEY_ESCAPE_COMMAND, SELECTION_CHANGE_COMMAND } from 'lexical';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { $isEquationNode } from './EquationNode';
import EquationEditor from '../plugins/EquationsPlugin/EquationEditor';
import KatexRenderer from '../plugins/EquationsPlugin/KatexRenderer';
const EquationComponent = ({
  equation,
  inline,
  nodeKey
}: {
  equation: string;
  inline: boolean;
  nodeKey: NodeKey;
}) => {
  const [editor] = useLexicalComposerContext();
  const [isEditable, setIsEditable] = useState<boolean>(editor.isEditable());
  const [equationValue, setEquationValue] = useState<{
    equation: string;
    inline: boolean;
  }>({
    equation,
    inline
  });
  const [showEquationEditor, setShowEquationEditor] = useState<boolean>(equationValue.equation === '');
  const inputRef = useRef(null);
  const onHide = useCallback((restoreSelection?: boolean) => {
    if (equationValue.equation !== '') setShowEquationEditor(false);
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isEquationNode(node)) {
        node.setEquation(equationValue.equation, equationValue.inline);
        if (restoreSelection) {
          node.selectNext(0, 0);
        }
      }
    });
  }, [editor, equationValue, nodeKey]);
  useEffect(() => {
    if (!showEquationEditor && equationValue.equation !== equation) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEquationValue({
        equation,
        inline
      });
    }
  }, [equation, equationValue, inline, showEquationEditor]);
  useEffect(() => {
    if (showEquationEditor) {
      return mergeRegister(editor.registerCommand(SELECTION_CHANGE_COMMAND, payload => {
        const activeElem = document.activeElement;
        const inputElem = inputRef.current;
        if (activeElem !== inputElem) {
          onHide();
        }
        return false;
      }, COMMAND_PRIORITY_HIGH), editor.registerCommand(KEY_ESCAPE_COMMAND, payload => {
        const activeElem = document.activeElement;
        const inputElem = inputRef.current;
        if (activeElem === inputElem) {
          onHide(true);
          return true;
        }
        return false;
      }, COMMAND_PRIORITY_HIGH));
    } else {
      return mergeRegister(editor.registerEditableListener(editable => {
        setIsEditable(editable);
      }), editor.registerUpdateListener(({
        editorState
      }) => {
        const isSelected = editorState.read(() => {
          const selection = $getSelection();
          return $isNodeSelection(selection) && selection.has(nodeKey) && selection.getNodes().length === 1;
        });
        if (isSelected) {
          setShowEquationEditor(true);
        }
      }));
    }
  }, [editor, nodeKey, onHide, showEquationEditor]);
  return <>
      {showEquationEditor ? <EquationEditor equation={equationValue.equation} setEquation={setEquationValue} inline={equationValue.inline} ref={inputRef} /> : <LexicalErrorBoundary onError={e => editor._onError(e)}>
          <KatexRenderer equation={equationValue.equation} inline={equationValue.inline} onDoubleCLick={() => {
        if (isEditable) {
          setShowEquationEditor(true);
        }
      }} />
        </LexicalErrorBoundary>}
    </>;
};
export default EquationComponent;